// @ts-check
import api from './api'
import {__, assoc, clone, filter, findIndex, forEach, head, identity, isNil, keys, map, merge, pickBy, pipe, remove, prop, propEq, values} from 'ramda'
import Debug from 'debug'
const debug = Debug('app:shared:jobs')
import algoliasearchHelper from 'algoliasearch-helper'
import algoliasearch from 'algoliasearch'
import { get } from 'lodash'

const jobs = ($http, $q, Auth, $httpParamSerializerJQLike) => {
  'ngInject'

  let schedule = {
    days: {},
    jobs: {}
  }
  let interviewSchedule = {
    days: {},
    jobs: {}
  }
  let jobTypes = []
  let positionsData
  let fetched = false
  let fetchedInterviews = false

  let getOpenJobsResult = null;
  let getOpenJobsLastFetched = new Date(Date.UTC(0,0,0,0,0,0)).getTime();
  let getOpenJobsCacheTime = 1000 * 60; // 1 minute

  let client

  const fetchJobs = (startDate, endDate) => {
    fetched = true
    return $http({
      method: 'POST',
      url: `${api.getBase()}/company/jobsSchedule`,
      data: {
        startDate,
        endDate
      }
    })
    .then(prop('data'))
    .then(setSchedule)
  }

  const companyJobs = (id, startDate, endDate) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/companies/${id}/roster`,
      data: {
        startDate,
        endDate
      }
    })
    .then(prop('data'))
  }

  /**
   * @param {Object} param0 
   * @param {string} param0.externalUserId
   * @param {string} param0.externalJobId
   * @param {number} param0.companyId - internal company id
   * @param {number} param0.shift
   * @returns 
   */
  const getIndividualRosterUpdates = ({ externalUserId, externalJobId, shift, companyId }) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/companies/${companyId}/individualRosterUpdates`,
      data: {
        userId: externalUserId,
        jobId: externalJobId,
        shift
      }
    })
    .then(prop('data'))
  }

  const getJobs = (startDate, endDate) => {
    if (fetched) {
      let def = $q.defer()
      def.resolve(schedule)
      return def.promise
    }
    return fetchJobs(startDate, endDate)
  }

  const getJobDetails = (id) => {
    return $http({
      url: `${api.getBase()}/jobs/${id}/details`,
      method: 'GET'
    })
    .then(prop('data'))
  }

  /**
   * Takes a companyId, a collection of userIds and a target date to return a map of the crewMemberIds to the jobs they are on for
   * given compay after the given target date.
   * @param {number} companyId
   * @param {number[]} userIdArray
   * @param {Date} targetDate
   */
  const getFutureShifts = ((companyId, userIdArray, targetDate) => {
    return $http({
      method: 'POST',
      url: `${api.getBaseV2()}/roster/${companyId}/futureShifts`,
      data: {
        workerIds: userIdArray,
        targetDate: targetDate,
      }
    })
      .then(prop('data'));
  });

  const getScheduleDetails = (id) => {
    return $http({
      url: `${api.getBaseV2()}/schedules/${id}`,
      method: 'GET'
    })
    .then(prop('data'))
  }

  const hideTemplate = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/template/hide`,
      data: {
        templateId: data
      }
    })
    .then(null, (err) => { throw err })
  }

  const getDetails = (jobId) => {
    // days->2012-mm-dd->jobId
    if (schedule.jobs && schedule.jobs[jobId]) {
      debug('pulling from cache, %o %o', schedule.jobs[jobId], filter(identity, map(prop(jobId), schedule.days)))
      let def = $q.defer()
      def.resolve({
        jobs: schedule.jobs[jobId],
        days: filter(identity, map(prop(jobId), schedule.days))
      })
      return def.promise
    }

    return fetchDetails(jobId)
  }

  const getShift = (jobId, shiftId) => {
    return getDetails(jobId)
      .then((data) => {
        data.shift = head(values(pickBy(x => x.shift === parseInt(shiftId, 10), data.days)))
        return data
      })
  }

  const getPositionsData = (companyId, positionId) => {
    if (positionsData && positionsData.company_id === companyId) {
      return Promise.resolve(positionsData)
    }
    let url = `${api.getBase()}/companies/${companyId}/positions`
    if (positionId) url = url + `/${positionId}` // optional position id
    return $http({
      method: 'GET',
      url: url
    })
    .then(prop('data'))
    .then(setPositionData)
  }

  const setPositionData = (response) => {
    positionsData = response
    return positionsData
  }

  const fetchPipelineData = () => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/routes/getPipeline`
    })
    .then(prop('data'))
  }

  const fetchPipelineAdmin = () => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/routes/getAdminPipeline`
    })
    .then(prop('data'))
  }

  const getJobTypes = () => {
    if (jobTypes.length) {
      let def = $q.defer()
      def.resolve(jobTypes)
      return def.promise
    }
    return fetchJobTypes()
  }

  const fetchJobTypes = () => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/job/getTypes`
    })
    .then(prop('data'), (err) => { throw err })
  }

  const fetchDetails = (jobId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/company/jobsSchedule`,
      data: {
        jobId
      }
    })
    .then(prop('data'))
    .then(job => {
      addJob(jobId, clone(job))
      job.days = filter(identity, map(prop(jobId), job.days))
      job.jobs = head(values(job.jobs))
      return clone(job)
    })
  }

  const addJob = (jobId, job) => {
    schedule.jobs[jobId] = clone(job.jobs[jobId])
    forEach((day) => { addDay(day, job.days[day]) }, keys(job.days))
  }

  const addDay = (k, v) => {
    if (isNil(schedule.days[k])) schedule.days[k] = {}
    schedule.days[k] = merge(schedule.days[k], v)
  }

  const updateCampaign = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/pipeline/update`,
      data: {
        campaignDetails: data
      }
    })
    .catch((e) => {
      debug('unable to create Campaign')
      throw e
    })
  }

  const fetchCampaign = (pipelineId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/pipeline/getCampaign`,
      data: {
        pipelineId
      }
    })
    .then(prop('data'))
    // source/getRecent
  }

  const removeJob = (id) => () => {
    let index = findIndex(propEq('objectId', id), schedule.jobs)
    schedule.jobs = remove(index, 1, jobs)
  }

  const cancelJob = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/cancel`,
      data: {
        jobId: data
      }
    })
  }

  const callOut = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/payroll/excuse`,
      data: {
        jobId: data.jobId,
        shift: data.shift,
        userId: data.userId
      }
    })
  }

  const noShow = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/addNoShow`,
      data: {
        jobId: data.jobId,
        shift: data.shift,
        userId: data.userId
      }
    })
  }

  const setSchedule = (response) => {
    schedule = response
    return schedule
  }

  const companyTimestamps = (id, since) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/companies/${id}/timestamps`,
      data: {
        since
      }
    })
    .then(prop('data'))
  }

  const getWaitlist = (jobId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/getWaitlist`,
      data: {
        jobId
      }
    })
    .then(prop('data'))
  }

  const getJobAttachedUsers = (jobIds, companyId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/getJobAttachedUsers`,
      data: {
        jobIds,
        companyId
      }
    })
    .then(prop('data'))
  }

  const getDirectInvites = (jobId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/getInvites`,
      data: {
        jobId
      }
    })
    .then(prop('data'))
  }

  const clock = (opts, users) => {
    const req = {
      method: 'POST',
      url: `${api.getBase()}/timestamps/putClockData`
    }

    const requests = map(
      pipe(
        assoc('userId', __, opts),
        assoc('data', __, req),
        $http
      ),
      users
    )

    return $q.all(requests)
  }

  const fetchOnboardingSessions = (startDate, endDate) => {
    fetchedInterviews = true
    return $http({
      method: 'POST',
      url: `${api.getBase()}/company/onboardingSchedule`,
      data: {
        startDate,
        endDate
      }
    })
    .then(prop('data'))
    .then(setInterviewSchedule)
  }

  const getInterviewDetails = (jobId) => {
    // days->2012-mm-dd->jobId
    if (interviewSchedule.jobs && interviewSchedule.jobs[jobId]) {
      debug('pulling from cache, %o %o', interviewSchedule.jobs[jobId], filter(identity, map(prop(jobId), interviewSchedule.days)))
      let def = $q.defer()
      def.resolve({
        jobs: interviewSchedule.jobs[jobId],
        days: filter(identity, map(prop(jobId), interviewSchedule.days))
      })
      return def.promise
    }

    return fetchInterviewDetails(jobId)
  }
  const fetchInterviewDetails = (jobId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/company/onboardingSchedule`,
      data: {
        jobId
      }
    })
    .then(prop('data'))
    .then(job => {
      addJob(jobId, clone(job))
      job.days = filter(identity, map(prop(jobId), job.days))
      job.jobs = head(values(job.jobs))
      return clone(job)
    })
  }

  const fetchFunnel = () => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/analytics/funnel`
    })
    .then(prop('data'))
  }

  const setInterviewSchedule = (response) => {
    interviewSchedule = response
    return interviewSchedule
  }

  const getPublicJobDetail = (pipelineId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/noauth/getPublicJobDetail`,
      data: {
        pipelineId
      }
    })
    .then(prop('data'))
  }

  const getPublicSearchKey = () => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/noauth/browseJobs`
    })
    .then((publicJobsKey) => {
      client = algoliasearch(CONFIG.ALGOLIA_APP_ID, publicJobsKey.data) // eslint-disable-line
    })
  }

  /** 
   * @typedef {object} OpenJob
   * @property {number} job_id
   * @property {string} external_id
   * @property {number} needed
   * @property {number} overfill
   * @property {number} days
   * @property {number} nick_delay
   * @property {number} is_application
   * @property {null} notes
   * @property {number} status
   * @property {string} created_at
   * @property {string} end_date_time
   * @property {string} start_date_time
   * @property {null} invited_worker_id
   * @property {number} whole_platform
   * @property {number} direct_hire
   * @property {number} wage
   * @property {number} job_type
   * @property {number} address_latitude
   * @property {number} address_longitude
   * @property {number} start_time_zone_offset
   * @property {number} end_time_zone_offset
   * @property {string} address
   * @property {string} city
   * @property {string} state
   * @property {string} zipcode
   * @property {string} title
   * @property {number} count
   * @property {string} company_name
   * @property {null} supervisor_phone_number
   * @property {object} pnt
   * @property {number} pnt.x
   * @property {number} pnt.y
   * @property {string} region
   */

  /**
   * 
   * @returns {Promise<OpenJob[]>}
   */
  const fetchOpenJobsData = async () => {
    if (Date.now() - getOpenJobsLastFetched < getOpenJobsCacheTime) {
      return getOpenJobsResult;
    } else {
      getOpenJobsLastFetched = Date.now();
      return getOpenJobsResult = $http({
        method: 'GET',
        url: `${api.getBase()}/job/getOpenJobs`
      })
      .then(res => res.data);
    }
  }

  const repost = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/repost`,
      data: {
        days: data.days,
        daysOfWeek: data.daysOfWeek,
        department: data.department,
        dsb: data.dsb || null,
        endDate: data.endDate,
        jobId: data.jobId,
        needed: data.needed,
        positionId: data.position_id,
        startDate: data.startDate,
        supervisor: data.supervisor,
        userIds: data.userIds || null,
        timezone: data.timezone,
        wage: data.wage,
        forceLegacyRepost: !!data.forceLegacyRepost
      }
    })
    .catch((e) => {
      debug('unable to create Job')
      throw e
    })
  }

  const repostSchedule = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/jobs/${data.jobId}/repost`,
      data: {
        startDate: data.startDate,
        endDate: data.endDate,
        days: data.days,
        userIds: data.userIds || null,
        dsb: data.dsb || null,
        needed: data.needed,
        timezone: data.timezone,
        scheduleId: data.scheduleId
      }
    })
    .catch((e) => {
      debug('unable to create Job')
      throw e
    })
  }

  const jobPulse = (jobId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/pulse`,
      data: {
        jobId: jobId
      }
    })
    .then(prop('data'))
  }

  const scheduleChange = (jobId, scheduleObj) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/${jobId}/schedule/change`,
      data: {
        scheduleObj
      }
    })
    .then(prop('data'))
    .catch((e) => {
      debug('Error changing schedule')
      throw e
    })
  }

  const editShiftTime = (data) => {
    const { addShifts, jobId, removeShifts, workerId } = data;
    return $http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBaseV2()}/jobs/${jobId}/worker/${workerId}/shifts`,
      data: {
        addShifts: [addShifts], // Epoch, milliseconds
        removeShifts: [removeShifts], // Epoch, milliseconds
      }
    })
      .then(prop('data'), (err) => { throw err })
  };

  const removeShifts = (data) => {
    // TODO: to be added on the backend side
    // reasonCode, reasonText,
    const { jobId, removeShifts, workerId } = data;
    return $http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBaseV2()}/jobs/${jobId}/worker/${workerId}/removeShifts`,
      data: {
        removeShifts: [removeShifts], // Epoch, milliseconds
        // reasonCode, // Integer
        // reasonText,// Text
      }
    })
      .then(prop('data'), (err) => { throw err })
  };

  const endAssignmentPreStart = (data) => {
    const { jobId, reasonCode, reasonText, workerId } = data;
    return $http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBaseV2()}/jobs/${jobId}/worker/${workerId}/cancelPreStart`,
      data: {
        reasonCode, // Integer
        reasonText, // Text
      }
    })
      .then(prop('data'), (err) => { throw err })
  };

  const changeAssignmentDateRange = (data) => {
    const {
      endDate,
      jobId,
      reasonCode,
      reasonText,
      startDate,
      workerId,
    } = data;
    return $http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBaseV2()}/jobs/${jobId}/worker/${workerId}/date_range`,
      data: {
        startDate: startDate, // Epoch, milliseconds
        endDate: endDate, // Epoch, milliseconds
        reasonCode: reasonCode, // Integer
        reasonText: reasonText, // Text
      }
    })
      .then(prop('data'), (err) => { throw err })
  };

  return {
    getFutureShifts,
    editShiftTime,
    changeAssignmentDateRange,
    endAssignmentPreStart,
    removeShifts,
    fetchJobs,
    repost,
    repostSchedule,
    callOut,
    cancelJob,
    clock,
    companyJobs,
    getIndividualRosterUpdates,
    companyTimestamps,
    fetchFunnel,
    updateCampaign,
    fetchPipelineData,
    fetchPipelineAdmin,
    fetchOpenJobsData,
    fetchCampaign,
    getWaitlist,
    getJobDetails,
    getScheduleDetails,
    getJobAttachedUsers,
    getPositionsData,
    getShift,
    getDirectInvites,
    getJobs,
    getDetails,
    getInterviewDetails,
    hideTemplate,
    jobPulse,
    noShow,
    removeJob,
    getJobTypes,
    getPublicJobDetail,
    getPublicSearchKey,
    scheduleChange
  }
}

jobs.$inject = ['$http', '$q', 'Auth']

export default jobs
